import React, { FC } from 'react';
import { Table, Icon, Typography } from '@scale/scaleui';
import { TaskAttemptReviewStatus, ReviewLevel, TaskReviewOutcome } from '@scale/shared/defaults';

export interface TaskInfo {
  attemptId?: string;
  reviewLevel?: ReviewLevel;
  reviewerRatingScore?: number;
  reviewStatus?: TaskAttemptReviewStatus;
  reviewOutcome?: TaskReviewOutcome;
}

interface TaskHistoryProps {
  taskInfo: TaskInfo[];
}

export const TaskHistoryTable: FC<TaskHistoryProps> = React.memo(function taskHistoryTable({
  taskInfo,
}: {
  taskInfo: TaskInfo[];
}) {
  const cols = [{ id: 'rowName', header: 'Attempt Review Level', enableSorting: false }];
  const filteredTaskInfo = taskInfo.filter((tInfo, i) => {
    return (
      i === 0 ||
      tInfo.reviewerRatingScore !== undefined ||
      tInfo.reviewStatus !== TaskAttemptReviewStatus.Pending ||
      (tInfo.reviewOutcome !== undefined && tInfo.reviewOutcome !== TaskReviewOutcome.Uncertain)
    );
  });

  cols.push(
    ...filteredTaskInfo.map((info, idx) => {
      let header = `L${info.reviewLevel ?? ''}`;
      if (idx === 0) {
        header += ' (Latest)';
      } else if (idx === filteredTaskInfo.length - 1) {
        header += ' (Oldest)';
      }
      return {
        id: info.attemptId ?? '',
        header,
        enableSorting: false,
      };
    }),
  );
  const hasAnyRRSScore = taskInfo.some(tInfo => tInfo.reviewerRatingScore !== undefined);
  const data: { [key: string]: React.ReactElement | string }[] = [
    {
      rowName: (
        <div className="flex flex-column gap-1 align-center">
          <Typography.Paragraph2>Task Status</Typography.Paragraph2>
          <Icon
            icon={['fas', 'info-circle']}
            size="sm"
            tooltip="Ultimately, was this task/review accepted?"
            tooltipProps={{ side: 'bottom', contentClassName: 'ml-8' }}
          />
        </div>
      ),
    },
    {
      rowName: (
        <div className="flex flex-column gap-1 align-center">
          <Typography.Paragraph2>Review Outcome</Typography.Paragraph2>
          <Icon
            icon={['fas', 'info-circle']}
            size="sm"
            tooltip="Did the reviewer accept/reject the task under review?"
            tooltipProps={{ side: 'bottom', contentClassName: 'ml-8' }}
          />
        </div>
      ),
    },
  ];
  if (hasAnyRRSScore) {
    data.splice(0, 0, { rowName: 'Review Score (1-5)' });
  }

  for (const info of taskInfo) {
    if (hasAnyRRSScore) {
      data[0][info.attemptId ?? ''] = info.reviewerRatingScore?.toString() ?? '';
      data[1][info.attemptId ?? ''] = info.reviewStatus ?? '';
      data[2][info.attemptId ?? ''] = info.reviewOutcome ?? '';
    } else {
      data[0][info.attemptId ?? ''] = info.reviewStatus ?? '';
      data[1][info.attemptId ?? ''] = info.reviewOutcome ?? '';
    }
  }

  return (
    <Table
      divideColumns={false}
      stripedRows={false}
      manualPagination={true}
      columns={cols}
      data={data}
      defaultPageSize={data.length}
    />
  );
});
